import React, { useMemo } from "react";

import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    ListItemText,
    MenuItem,
    OutlinedInput,
    TextField,
} from "@mui/material";

import {
    LeadReportGroupBy,
    LeadReportStatus,
} from "helpers/leads/generalReports";

import { validateRange } from "../LeadGeneralReportsPage.helpers";
import * as Styled from "../LeadGeneralReportsPage.styled";
import {
    ALL_STATUSES,
    LeadGeneralReportFilters,
    STATUS_COLORS,
} from "../LeadGeneralReportsPage.types";

interface ConnectionOption {
    id: number;
    name: string;
}

interface Props {
    filters: LeadGeneralReportFilters;
    onChange: (next: LeadGeneralReportFilters) => void;
    onUpdate: () => void;
    isLoading: boolean;
    connectionOptions?: ConnectionOption[];
}

const DATE_PRESETS: {
    id: LeadGeneralReportFilters["datePreset"];
    label: string;
}[] = [
    { id: "last7", label: "Last 7 days" },
    { id: "last30", label: "Last 30 days" },
    { id: "last90", label: "Last 90 days" },
    { id: "custom", label: "Custom range" },
];

const STATUSES = ALL_STATUSES;

const GROUP_BY_OPTIONS: { id: LeadReportGroupBy; label: string }[] = [
    { id: "connection", label: "By Connection" },
    { id: "aggregated", label: "Aggregated" },
];

const checkboxSx = {
    p: 0.5,
    color: "#CBD5E1",
    "&.Mui-checked": {
        color: "#ED6C02",
    },
};

/**
 * `selectedMenu` (MUI default) re-targets the active item to the *last* selected
 * option on every render, which auto focuses it and scrolls the menu to the
 * bottom after "Select All". `menu` keeps the active item stable.
 */
const MULTI_SELECT_MENU_PROPS = { variant: "menu" as const };

/**
 * The zero width space keeps the select display box from collapsing to 0px
 * height when no filter is selected, which would make the select unclickable.
 */
const renderSelectValue = (text: string) => (
    <Styled.SelectValueText title={text || undefined}>
        {text || "\u200B"}
    </Styled.SelectValueText>
);

interface SelectActionsProps {
    selectAllLabel: string;
    onSelectAll: () => void;
    onClear: () => void;
    selectAllDisabled?: boolean;
    clearDisabled?: boolean;
}

/**
 * Action row rendered at the top of a multi select menu.
 * It is not a MenuItem on purpose: it must not be selectable, hoverable
 * or close the menu when one of its actions is used.
 */
const SelectActions: React.FC<SelectActionsProps> & {
    muiSkipListHighlight?: boolean;
} = ({
    selectAllLabel,
    onSelectAll,
    onClear,
    selectAllDisabled,
    clearDisabled,
}) => (
    <Styled.SelectActionsRow onKeyDown={(event) => event.stopPropagation()}>
        <Styled.SelectActionButton
            type="button"
            disabled={selectAllDisabled}
            onClick={onSelectAll}
        >
            {selectAllLabel}
        </Styled.SelectActionButton>
        <Styled.SelectActionButton
            type="button"
            disabled={clearDisabled}
            onClick={onClear}
        >
            Clear
        </Styled.SelectActionButton>
    </Styled.SelectActionsRow>
);

// Keeps MenuList from treating the action row as the initially highlighted item.
SelectActions.muiSkipListHighlight = true;

const FilterBar: React.FC<Props> = ({
    filters,
    onChange,
    onUpdate,
    isLoading,
    connectionOptions = [],
}) => {
    const validation = validateRange(filters);

    const statusRenderValue = useMemo(() => {
        if (!filters.statuses.length) return "";
        if (filters.statuses.length === STATUSES.length) return "All Statuses";
        return filters.statuses
            .map((status) => STATUS_COLORS[status].label)
            .join(", ");
    }, [filters.statuses]);

    const connectionRenderValue = useMemo(() => {
        const selectedCount = filters.connectionIds?.length ?? 0;
        if (!selectedCount) return "";
        if (
            connectionOptions.length > 0 &&
            selectedCount === connectionOptions.length
        ) {
            return "All Connections";
        }
        return connectionOptions
            .filter((option) => filters.connectionIds?.includes(option.id))
            .map((option) => option.name)
            .join(", ");
    }, [filters.connectionIds, connectionOptions]);

    return (
        <Styled.FilterCard elevation={0}>
            <Styled.FilterRow>
                {/* DATE RANGE */}
                <Styled.FilterField $grow={1.2}>
                    <Styled.StyledFormControl size="small">
                        <Styled.StyledInputLabel id="date-range-label" shrink>
                            Date Range
                        </Styled.StyledInputLabel>
                        <Styled.StyledSelect
                            labelId="date-range-label"
                            size="small"
                            notched
                            input={<OutlinedInput notched label="Date Range" />}
                            value={filters.datePreset}
                            onChange={(event) => {
                                const preset = event.target
                                    .value as LeadGeneralReportFilters["datePreset"];
                                onChange({
                                    ...filters,
                                    datePreset: preset,
                                    startDate:
                                        preset === "custom"
                                            ? filters.startDate
                                            : null,
                                    endDate:
                                        preset === "custom"
                                            ? filters.endDate
                                            : null,
                                });
                            }}
                        >
                            {DATE_PRESETS.map((preset) => (
                                <MenuItem key={preset.id} value={preset.id}>
                                    {preset.label}
                                </MenuItem>
                            ))}
                        </Styled.StyledSelect>
                    </Styled.StyledFormControl>
                    {filters.datePreset === "custom" && (
                        <Styled.DateInputRow direction="row" gap={1} mt={1}>
                            <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={filters.startDate ?? ""}
                                onChange={(event) =>
                                    onChange({
                                        ...filters,
                                        startDate: event.target.value,
                                    })
                                }
                            />
                            <TextField
                                type="date"
                                size="small"
                                fullWidth
                                value={filters.endDate ?? ""}
                                onChange={(event) =>
                                    onChange({
                                        ...filters,
                                        endDate: event.target.value,
                                    })
                                }
                            />
                        </Styled.DateInputRow>
                    )}
                </Styled.FilterField>

                {/* CONNECTIONS */}
                <Styled.FilterField>
                    <Styled.StyledFormControl size="small">
                        <Styled.StyledInputLabel id="connections-label" shrink>
                            Connections
                        </Styled.StyledInputLabel>
                        <Styled.StyledSelect
                            labelId="connections-label"
                            size="small"
                            multiple
                            displayEmpty
                            notched
                            input={
                                <OutlinedInput notched label="Connections" />
                            }
                            MenuProps={MULTI_SELECT_MENU_PROPS}
                            disabled={connectionOptions.length === 0}
                            value={filters.connectionIds ?? []}
                            onChange={(event) => {
                                const raw = event.target.value as number[];
                                onChange({
                                    ...filters,
                                    connectionIds: raw.length
                                        ? raw
                                        : undefined,
                                });
                            }}
                            renderValue={() =>
                                renderSelectValue(connectionRenderValue)
                            }
                        >
                            <SelectActions
                                selectAllLabel="Select All Connections"
                                selectAllDisabled={
                                    connectionOptions.length === 0 ||
                                    filters.connectionIds?.length ===
                                        connectionOptions.length
                                }
                                clearDisabled={!filters.connectionIds?.length}
                                onSelectAll={() =>
                                    onChange({
                                        ...filters,
                                        connectionIds: connectionOptions.map(
                                            (option) => option.id,
                                        ),
                                    })
                                }
                                onClear={() =>
                                    onChange({
                                        ...filters,
                                        connectionIds: undefined,
                                    })
                                }
                            />
                            {connectionOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    <Checkbox
                                        size="small"
                                        sx={checkboxSx}
                                        checked={
                                            filters.connectionIds?.includes(
                                                option.id,
                                            ) ?? false
                                        }
                                    />
                                    <ListItemText primary={option.name} />
                                </MenuItem>
                            ))}
                            {connectionOptions.length === 0 && (
                                <MenuItem disabled value="">
                                    No connections found
                                </MenuItem>
                            )}
                        </Styled.StyledSelect>
                    </Styled.StyledFormControl>
                </Styled.FilterField>

                {/* STATUS */}
                <Styled.FilterField>
                    <Styled.StyledFormControl size="small">
                        <Styled.StyledInputLabel id="status-label" shrink>
                            Status
                        </Styled.StyledInputLabel>
                        <Styled.StyledSelect
                            labelId="status-label"
                            size="small"
                            multiple
                            displayEmpty
                            notched
                            input={<OutlinedInput notched label="Status" />}
                            MenuProps={MULTI_SELECT_MENU_PROPS}
                            value={filters.statuses}
                            onChange={(event) => {
                                onChange({
                                    ...filters,
                                    statuses: event.target
                                        .value as LeadReportStatus[],
                                });
                            }}
                            renderValue={() =>
                                renderSelectValue(statusRenderValue)
                            }
                        >
                            <SelectActions
                                selectAllLabel="Select All Statuses"
                                selectAllDisabled={
                                    filters.statuses.length === STATUSES.length
                                }
                                clearDisabled={!filters.statuses.length}
                                onSelectAll={() =>
                                    onChange({
                                        ...filters,
                                        statuses: [...STATUSES],
                                    })
                                }
                                onClear={() =>
                                    onChange({ ...filters, statuses: [] })
                                }
                            />
                            {STATUSES.map((status) => {
                                const meta = STATUS_COLORS[status];
                                const checked =
                                    filters.statuses.includes(status);
                                return (
                                    <MenuItem key={status} value={status}>
                                        <Checkbox
                                            size="small"
                                            checked={checked}
                                            sx={checkboxSx}
                                        />
                                        <ListItemText primary={meta.label} />
                                    </MenuItem>
                                );
                            })}
                        </Styled.StyledSelect>
                    </Styled.StyledFormControl>
                </Styled.FilterField>

                {/* GROUPING */}
                <Styled.FilterField>
                    <Styled.StyledFormControl size="small">
                        <Styled.StyledInputLabel id="grouping-label" shrink>
                            Grouping
                        </Styled.StyledInputLabel>
                        <Styled.StyledSelect
                            labelId="grouping-label"
                            size="small"
                            notched
                            input={<OutlinedInput notched label="Grouping" />}
                            value={filters.groupBy}
                            onChange={(event) =>
                                onChange({
                                    ...filters,
                                    groupBy: event.target
                                        .value as LeadReportGroupBy,
                                })
                            }
                        >
                            {GROUP_BY_OPTIONS.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Styled.StyledSelect>
                    </Styled.StyledFormControl>
                </Styled.FilterField>

                {/* UPDATE VIEW */}
                <Styled.FilterField $grow={0} $fullOnMobile>
                    {isLoading && (
                        <div className="circle-loader">
                            <CircularProgress size={20} />
                        </div>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onUpdate}
                        style={{
                            minWidth: "unset !important",
                        }}
                    >
                        Apply Filters
                    </Button>
                </Styled.FilterField>
            </Styled.FilterRow>

            {!validation.ok && (
                <Box
                    sx={{
                        color: "#DC2626",
                        fontSize: 12,
                        fontWeight: 500,
                        mt: 1,
                    }}
                >
                    {validation.message}
                </Box>
            )}
        </Styled.FilterCard>
    );
};

export default FilterBar;
